import { useEffect, useRef } from "react";
import Flatpickr from "react-flatpickr";
import type { DateTimePickerHandle, DateTimePickerProps } from "react-flatpickr";
import { getHolidays } from "features/Holidays/services/HolidayService";
import type { Holiday } from "features/Holidays/types/holiday.types";

const STYLE_ID = "app-flatpickr-holiday-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    .flatpickr-day.is-holiday {
      background-color: #fff3f0;
      color: #c44e29;
    }
    .flatpickr-day.is-holiday:hover {
      background-color: #ffd9d0;
    }
  `;
  document.head.appendChild(style);
}

function toYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function applyHolidaysToCalendar(
  fp: { calendarContainer?: HTMLElement },
  holidayMap: Record<string, string>,
) {
  const container = fp.calendarContainer;
  if (!container) return;
  container.querySelectorAll<HTMLElement>(".flatpickr-day").forEach((el) => {
    const date = (el as any).dateObj as Date | undefined;
    if (!date) return;
    const ymd = toYmd(date);
    const name = holidayMap[ymd];
    if (name) {
      el.classList.add("is-holiday");
      el.title = name;
    }
  });
}

/**
 * Drop-in replacement for <Flatpickr> with app-level enhancements.
 * Highlights holidays with a background colour, shows the holiday name as a
 * tooltip, and disables selection of holiday dates.
 * Holidays are fetched per-year and cached for the component's lifetime.
 */
const AppFlatpickr = ({ options, ...rest }: DateTimePickerProps) => {
  const holidayMap = useRef<Record<string, string>>({});
  const loadedYears = useRef<Set<number>>(new Set());
  const fpHandle = useRef<DateTimePickerHandle>(null);

  const loadYear = async (year: number) => {
    if (loadedYears.current.has(year)) return;
    loadedYears.current.add(year);
    try {
      const res = await getHolidays({ year, limit: 250 });
      const rows: Holiday[] = (res as any)?.data?.rows ?? [];
      rows.forEach((h) => {
        holidayMap.current[h.date.slice(0, 10)] = h.name;
      });
      const fp = fpHandle.current?.flatpickr;
      if (fp?.isOpen) {
        applyHolidaysToCalendar(fp as any, holidayMap.current);
        // Re-set disable so Flatpickr re-evaluates which days are disabled
        // for the already-open calendar after async holiday data arrives.
        fp.set("disable", [...(fp.config.disable ?? [])]);
      }
    } catch {
      // Holiday highlighting is non-critical — ignore errors silently
    }
  };

  useEffect(() => {
    void loadYear(new Date().getFullYear());
  }, []);

  const userOnDayCreate = options?.onDayCreate;
  const userOnOpen = options?.onOpen;
  const userOnMonthChange = options?.onMonthChange;
  const userOnYearChange = options?.onYearChange;

  const mergedOptions: DateTimePickerProps["options"] = {
    ...options,

    disable: [
      ...((options?.disable as any[]) ?? []),
      (date: Date) => toYmd(date) in holidayMap.current,
    ],

    onDayCreate(selectedDates, dateStr, fp, dayElem) {
      const date = (dayElem as any).dateObj as Date | undefined;
      if (date) {
        const ymd = toYmd(date);
        const name = holidayMap.current[ymd];
        if (name) {
          dayElem.classList.add("is-holiday");
          dayElem.title = name;
        }
      }
      if (typeof userOnDayCreate === "function") {
        (userOnDayCreate as (...a: any[]) => void)(selectedDates, dateStr, fp, dayElem);
      }
    },

    onOpen(selectedDates, dateStr, fp) {
      void loadYear(fp.currentYear);
      requestAnimationFrame(() => {
        applyHolidaysToCalendar(fp as any, holidayMap.current);
      });
      if (typeof userOnOpen === "function") {
        (userOnOpen as (...a: any[]) => void)(selectedDates, dateStr, fp);
      }
    },

    onMonthChange(selectedDates, dateStr, fp) {
      void loadYear(fp.currentYear);
      requestAnimationFrame(() => {
        applyHolidaysToCalendar(fp as any, holidayMap.current);
      });
      if (typeof userOnMonthChange === "function") {
        (userOnMonthChange as (...a: any[]) => void)(selectedDates, dateStr, fp);
      }
    },

    onYearChange(selectedDates, dateStr, fp) {
      void loadYear(fp.currentYear);
      requestAnimationFrame(() => {
        applyHolidaysToCalendar(fp as any, holidayMap.current);
      });
      if (typeof userOnYearChange === "function") {
        (userOnYearChange as (...a: any[]) => void)(selectedDates, dateStr, fp);
      }
    },
  };

  return <Flatpickr ref={fpHandle} options={mergedOptions} {...rest} />;
};

export default AppFlatpickr;
